import { playMotion } from './avatar.js';
import { setEmotion } from './expression-manager.js';
import { updateAmplitude, stopSpeaking } from './lip-sync.js';

type AvatarCommand =
  | { type: 'set-emotion'; emotion: string }
  | { type: 'set-mouth'; openness: number }
  | { type: 'start-speaking' }
  | { type: 'stop-speaking' }
  | { type: 'set-idle'; enabled: boolean };

type AvatarEvent =
  | { type: 'ready' }
  | { type: 'model-loaded'; modelName: string }
  | { type: 'error'; message: string };

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const RECONNECT_DELAY = 2000;

export function connect(port: number): void {
  const url = `ws://localhost:${port}`;

  ws = new WebSocket(url);

  ws.onopen = () => {
    console.log('[ws-client] Connected to orchestrator');
    sendEvent({ type: 'ready' });
  };

  ws.onmessage = (event) => {
    try {
      const command: AvatarCommand = JSON.parse(event.data);
      handleCommand(command);
    } catch (err) {
      console.error('[ws-client] Failed to parse command:', err);
    }
  };

  ws.onclose = () => {
    console.warn('[ws-client] Disconnected, reconnecting...');
    scheduleReconnect(port);
  };

  ws.onerror = (err) => {
    console.error('[ws-client] WebSocket error:', err);
  };
}

function handleCommand(command: AvatarCommand): void {
  switch (command.type) {
    case 'set-emotion':
      setEmotion(command.emotion);
      break;
    case 'set-mouth':
      updateAmplitude(command.openness);
      break;
    case 'start-speaking':
      // Avatar is now speaking — lip-sync will be driven by set-mouth commands
      break;
    case 'stop-speaking':
      stopSpeaking();
      break;
    case 'set-idle':
      if (command.enabled) {
        playMotion('idle', 0);
      }
      break;
  }
}

function sendEvent(event: AvatarEvent): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(event));
  }
}

export function sendModelLoaded(modelName: string): void {
  sendEvent({ type: 'model-loaded', modelName });
}

export function sendError(message: string): void {
  sendEvent({ type: 'error', message });
}

function scheduleReconnect(port: number): void {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect(port);
  }, RECONNECT_DELAY);
}

export function disconnect(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
}
